import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { driver, type Driver, type DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useAuth } from '../../core/auth'

// Interactive first-run product tour for the DEMO account only.
//
// It is a click-through: the user is asked to open each tab themselves, and an
// explanation appears once they land on the page. Advancement on the "open this
// tab" steps is driven by the actual route change (DashboardLayout stays mounted
// across navigation, so this component — and the driver instance — persist).
//
// Triggered by the login action: auth.login sets TOUR_START_FLAG when the demo
// user signs in. A cookie-restore on refresh does NOT set it, so the tour only
// fires on an explicit demo login, never for real operators.
export const TOUR_START_FLAG = 'lngperf:start_tour'
const DEMO_EMAIL = 'demo'

// Tabs in sidebar order. The first (Dashboard) is the post-login landing page,
// so it is explained without a click prompt.
const STOPS: { path: string; label: string; desc: string }[] = [
  {
    path: '/lng-ops',
    label: 'Dashboard',
    desc: 'Your starting point — fleet warranty health, the fleet map, and your most recent voyages at a glance.',
  },
  {
    path: '/lng-ops/live-view',
    label: 'Live View',
    desc: 'Track your fleet on a live map. Hover any ship for its live detail, and you can see every LNG import and export terminal. Have a look around — then click Next.',
  },
  {
    path: '/lng-ops/voyage-performance',
    label: 'Voyage Perf.',
    desc: 'The core of the product: charter-party warranty per voyage — boil-off and fuel against the guarantee, shown as Excess or Savings.',
  },
  {
    path: '/lng-ops/calculations',
    label: 'Calculations',
    desc: 'See exactly how each warranty result is built — step by step, with the charter-party clause behind every figure.',
  },
  {
    path: '/lng-ops/vessel-reports',
    label: 'Vessel Reports',
    desc: 'Every noon report in one place, with fully configurable columns.',
  },
  {
    path: '/lng-ops/graphs',
    label: 'Graphs',
    desc: 'Build charts from ready-made presets or any column you choose.',
  },
  {
    path: '/lng-ops/reports',
    label: 'Reports',
    desc: 'Charterer-ready spreadsheets — export to PDF, Excel or CSV in a click.',
  },
  {
    path: '/lng-ops/scheduler',
    label: 'Scheduler',
    desc: 'Automate report delivery — daily, weekly, monthly, or once per completed voyage.',
  },
]

type StepKind = 'welcome' | 'explain' | 'prompt' | 'final'
interface StepMeta {
  kind: StepKind
  path?: string
}

// Build the driver step list + a parallel meta list (same indices) so the route
// listener can tell which step is a "click this tab" prompt and what path it
// expects. Indices MUST stay aligned, so we never filter one without the other.
function buildSteps(): { steps: DriveStep[]; metas: StepMeta[] } {
  const steps: DriveStep[] = []
  const metas: StepMeta[] = []

  steps.push({
    popover: {
      title: 'Welcome to LNG Performance',
      description:
        "Let's take a quick walk through the product. I'll ask you to open each tab — click it in the menu and I'll explain what's inside. Click Next to begin.",
      showButtons: ['next', 'close'],
    },
  })
  metas.push({ kind: 'welcome' })

  STOPS.forEach((stop, i) => {
    if (i > 0) {
      // "Open this tab" — no Next button, so the user must click the real tab.
      steps.push({
        element: `[data-tour="${stop.path}"]`,
        popover: {
          title: stop.label,
          description: `Click <b>${stop.label}</b> in the menu to open it.`,
          side: 'right',
          align: 'start',
          showButtons: ['close'],
        },
      })
      metas.push({ kind: 'prompt', path: stop.path })
    }
    steps.push({
      popover: {
        title: stop.label,
        description: stop.desc,
        showButtons: ['next', 'close'],
      },
    })
    metas.push({ kind: 'explain', path: stop.path })
  })

  steps.push({
    popover: {
      title: "You're all set",
      description:
        'That’s the tour. Explore freely — press Ctrl + K to jump anywhere, and use the feedback button in the corner any time.',
      showButtons: ['next', 'close'],
    },
  })
  metas.push({ kind: 'final' })

  return { steps, metas }
}

const { steps: STEPS, metas: METAS } = buildSteps()

export default function OnboardingTour() {
  const { user } = useAuth()
  const location = useLocation()
  const driverRef = useRef<Driver | null>(null)

  // Start the tour once, on demo login.
  useEffect(() => {
    if (!user || user.email !== DEMO_EMAIL) return

    let pending = false
    try {
      pending = sessionStorage.getItem(TOUR_START_FLAG) === '1'
    } catch {
      return
    }
    if (!pending || driverRef.current) return

    const timer = window.setTimeout(() => {
      if (!document.querySelector('[data-tour="sidebar"]')) return

      const d = driver({
        showProgress: true,
        // Never auto-close on an outside click, and let the user interact with
        // the page during a step (hover ships, open terminals, explore). The
        // overlay is made non-interactive via CSS (.driver-overlay), so the only
        // way to exit is the Close button. Advancing a "click this tab" step
        // still works because the real nav link is clicked.
        allowClose: false,
        disableActiveInteraction: false,
        overlayColor: '#000',
        overlayOpacity: 0.5,
        stagePadding: 6,
        stageRadius: 8,
        popoverClass: 'lngperf-tour',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Done',
        progressText: '{{current}} of {{total}}',
        steps: STEPS,
        onDestroyed: () => {
          driverRef.current = null
        },
      })
      driverRef.current = d
      d.drive()

      try {
        sessionStorage.removeItem(TOUR_START_FLAG)
      } catch {
        /* noop */
      }
    }, 650)

    return () => window.clearTimeout(timer)
  }, [user])

  // Advance a "click this tab" step once the user actually navigates there.
  useEffect(() => {
    const d = driverRef.current
    if (!d || !d.isActive()) return
    const idx = d.getActiveIndex()
    if (idx == null) return
    const meta = METAS[idx]
    if (meta?.kind === 'prompt' && meta.path === location.pathname) {
      // Small delay so the new page begins rendering under the explanation.
      window.setTimeout(() => {
        const cur = driverRef.current
        if (cur?.isActive() && cur.getActiveIndex() === idx) cur.moveNext()
      }, 300)
    }
  }, [location.pathname])

  // Tear down if the layout unmounts (e.g. logout) mid-tour.
  useEffect(() => {
    return () => {
      if (driverRef.current?.isActive()) driverRef.current.destroy()
      driverRef.current = null
    }
  }, [])

  return null
}
